<template>
  <page-header-wrapper>
    <template v-slot:content>
      <div class="home-container" :style="themeVars">
        <!-- 主欢迎区域 -->
        <div class="welcome-section">
          <div class="welcome-content">
            <h1 class="welcome-title">{{ $t('views.home.welcomeTitle') }}</h1>
            <p class="welcome-subtitle">{{ $t('views.home.welcomeSubtitle') }}</p>
            <div class="welcome-meta">
              <a-tag color="success" style="color: #333">{{ $t('views.home.systemRunning') }}</a-tag>
              <a-tag color="processing" style="color: #333">{{ $t('views.home.versionTag', { version: 'v1.0.0' }) }}</a-tag>
            </div>
          </div>
        </div>

        <!-- 内容区域 - 优化排版 -->
        <div class="content-layout">
          <a-row :gutter="24">
            <!-- 左侧内容区域 -->
            <a-col :span="16">
              <div class="left-content">
                <!-- 温馨提醒卡片 -->
                <a-card :title="$t('views.home.remindersTitle')" class="content-card enhanced-card" :bordered="false">
                  <div class="card-content">
                    <a-list :dataSource="reminders" :split="false">
                      <a-list-item slot="renderItem" slot-scope="item">
                        <a-list-item-meta>
                          <div slot="avatar">
                            <a-icon :type="item.icon" :style="{ color: item.color, fontSize: '20px' }" />
                          </div>
                          <div slot="title" class="reminder-title">{{ item.title }}</div>
                          <div slot="description">
                            <p class="reminder-date">{{ item.date }}</p>
                            <p class="reminder-content">{{ item.content }}</p>
                          </div>
                        </a-list-item-meta>
                      </a-list-item>
                    </a-list>
                  </div>
                </a-card>

                <!-- 系统状态卡片 -->
                <a-card :title="$t('views.home.systemStatusTitle')" class="content-card enhanced-card" :bordered="false" style="margin-top: 24px;">
                  <div class="card-content">
                    <a-row :gutter="16">
                      <a-col :span="12">
                        <div class="status-block">
                          <div class="status-icon-wrapper">
                            <a-icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" class="status-main-icon" />
                          </div>
                          <div class="status-info">
                            <h4>{{ $t('views.home.runStatusLabel') }}</h4>
                            <p class="status-value success">{{ $t('views.home.systemRunning') }}</p>
                          </div>
                        </div>
                      </a-col>
                      <a-col :span="12">
                        <div class="status-block">
                          <div class="status-icon-wrapper">
                            <a-icon type="sync" spin class="status-main-icon theme-icon" />
                          </div>
                          <div class="status-info">
                            <h4>{{ $t('views.home.lastUpdate') }}</h4>
                            <p class="status-value">{{ currentTimeText }}</p>
                          </div>
                        </div>
                      </a-col>
                    </a-row>
                  </div>
                </a-card>
              </div>
            </a-col>

            <!-- 右侧内容区域 -->
            <a-col :span="8">
              <div class="right-content">
                <!-- 技术支持卡片 -->
                <a-card :title="$t('views.home.supportTitle')" class="content-card enhanced-card" :bordered="false">
                  <div class="card-content">
                    <div class="support-section">
                      <h4><a-icon type="phone" class="section-icon" /> {{ $t('views.home.phone') }}</h4>
                      <p class="support-detail">18652016876</p>
                    </div>
                    <div class="support-section">
                      <h4><a-icon type="mail" class="section-icon" /> {{ $t('views.home.email') }}</h4>
                      <p class="support-detail">841656826@qq.com</p>
                    </div>
                    <div class="support-section">
                      <h4><a-icon type="clock-circle" class="section-icon" /> {{ $t('views.home.serviceHours') }}</h4>
                      <p class="support-detail">{{ $t('views.home.weekdays') }}</p>
                      <p class="support-detail">{{ $t('views.home.serviceTimeRange') }}</p>
                    </div>
                  </div>
                </a-card>

                <!-- 版本信息卡片 -->
                <a-card :title="$t('views.home.versionInfoTitle')" class="content-card enhanced-card" :bordered="false" style="margin-top: 24px;">
                  <div class="card-content">
                    <div class="version-section">
                      <div class="version-badge">
                        <a-badge status="success" />
                        <span>v1.0.0</span>
                      </div>
                      <p class="version-desc">{{ $t('views.home.stableVersion') }}</p>
                      <!--                      <div class="version-date">发布日期：2024-01-15</div>-->
                    </div>
                  </div>
                </a-card>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- 底部信息 -->
        <div class="footer-info">
          <a-divider />
          <div class="system-info">
            <p>{{ $t('views.home.footer') }}</p>
          </div>
        </div>
      </div>
    </template>
  </page-header-wrapper>
</template>

<script>
import { PageHeaderWrapper } from '@/components/ProLayout'
import { Row, Col, Card, Icon, Divider, Tag, List, Badge } from 'ant-design-vue'
import { baseMixin } from '@/store/app-mixin'

export default {
  name: 'Index',
  mixins: [baseMixin],
  components: {
    PageHeaderWrapper,
    [Row.name]: Row,
    [Col.name]: Col,
    [Card.name]: Card,
    [Icon.name]: Icon,
    [Divider.name]: Divider,
    [Tag.name]: Tag,
    [List.name]: List,
    [List.Item.name]: List.Item,
    [Badge.name]: Badge
  },
  data () {
    return {
      currentTime: new Date()
    }
  },
  computed: {
    dateLocale () {
      return this.$i18n.locale === 'en-US' ? 'en-US' : 'zh-CN'
    },
    currentDate () {
      return this.currentTime.toLocaleDateString(this.dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })
    },
    currentTimeText () {
      return this.currentTime.toLocaleTimeString(this.dateLocale)
    },
    themeVars () {
      const color = this.primaryColor || '#101010'
      return {
        '--home-primary-color': color,
        '--home-primary-light': this.hexToRgba(color, 0.14),
        '--home-primary-lighter': this.hexToRgba(color, 0.06),
        '--home-primary-soft': this.hexToRgba(color, 0.08)
      }
    },
    reminders () {
      return [
        {
          icon: 'security-scan',
          color: this.primaryColor || '#101010',
          title: this.$t('views.home.reminder.securityTitle'),
          date: this.currentDate,
          content: this.$t('views.home.reminder.securityContent')
        },
        {
          icon: 'bulb',
          color: '#faad14',
          title: this.$t('views.home.reminder.tipTitle'),
          date: this.currentDate,
          content: this.$t('views.home.reminder.tipContent')
        }
      ]
    }
  },
  methods: {
    hexToRgba (hex, alpha) {
      if (!hex) {
        return `rgba(16, 16, 16, ${alpha})`
      }
      let normalized = String(hex).replace('#', '')
      if (normalized.length === 3) {
        normalized = normalized.split('').map(char => char + char).join('')
      }
      const r = parseInt(normalized.substring(0, 2), 16)
      const g = parseInt(normalized.substring(2, 4), 16)
      const b = parseInt(normalized.substring(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
  },
  mounted () {
    // 每分钟更新时间
    setInterval(() => {
      this.currentTime = new Date()
    }, 60000)
  }
}
</script>

<style lang="less" scoped>
.home-container {
  padding: 24px;
}

.welcome-section {
  background: linear-gradient(120deg, var(--home-primary-light) 0%, var(--home-primary-lighter) 100%);
  border-radius: 12px;
  padding: 48px 40px;
  margin-bottom: 24px;
  color: #333;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .welcome-title {
    font-size: 32px;
    font-weight: 600;
    margin-bottom: 16px;
    color: var(--home-primary-color);
  }

  .welcome-subtitle {
    font-size: 18px;
    opacity: 0.9;
    margin-bottom: 24px;
  }

  .welcome-meta {
    .ant-tag {
      margin: 0 8px;
    }
  }
}

.content-layout {
  margin-bottom: 24px;

  .enhanced-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    ::v-deep .ant-card-head {
      border-bottom: 1px solid #f0f0f0;
      padding: 0 24px;

      .ant-card-head-title {
        font-size: 16px;
        font-weight: 500;
        padding: 16px 0;
      }
    }

    ::v-deep .ant-card-body {
      padding: 24px;
    }
  }

  .left-content,
  .right-content {
    .card-content {
      .reminder-title {
        font-weight: 500;
        color: #333;
        margin-bottom: 4px;
      }

      .reminder-date {
        color: #999;
        font-size: 12px;
        margin: 4px 0;
      }

      .reminder-content {
        color: #666;
        margin: 8px 0 0 0;
        line-height: 1.5;
      }

      .status-block {
        display: flex;
        align-items: center;
        padding: 16px;
        background: #fafafa;
        border-radius: 6px;
        transition: all 0.3s;

        &:hover {
          background: var(--home-primary-soft);
        }

        .status-icon-wrapper {
          margin-right: 16px;

          .status-main-icon {
            font-size: 24px;

            &.theme-icon {
              color: var(--home-primary-color);
            }
          }
        }

        .status-info {
          h4 {
            margin: 0 0 8px 0;
            font-size: 14px;
            color: #666;
          }

          .status-value {
            margin: 0;
            font-weight: 500;
            color: #333;

            &.success {
              color: #52c41a;
            }
          }
        }
      }

      .support-section {
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }

        .section-icon {
          margin-right: 8px;
          color: var(--home-primary-color);
        }

        h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
          color: #333;
        }

        .support-detail {
          margin: 0 0 4px 0;
          color: #666;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .version-section {
        text-align: center;

        .version-badge {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 12px;

          span {
            margin-left: 8px;
            font-weight: 500;
            font-size: 16px;
            color: #333;
          }
        }

        .version-desc {
          margin: 0 0 8px 0;
          color: #666;
        }

        .version-date {
          color: #999;
          font-size: 12px;
        }
      }
    }
  }
}

.footer-info {
  text-align: center;
  margin-top: 24px;

  .system-info {
    p {
      color: #999;
      margin-bottom: 0;
    }
  }
}
</style>
